import React, { useCallback, useMemo, useState } from 'react';
import { History, Loader2, X } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useDebugMode } from '../../../contexts/DebugModeContext';
import { apiService } from '../../../services/ApiService';
import { Message } from './types';

interface ClickEvent {
  id: number;
  uid: string;
  session_id: string | null;
  label: string;
  path: string | null;
  detail: Record<string, string> | null;
  clicked_at: string;
}

type Row = { at: Date; kind: 'click' | 'user' | 'bot'; label: string; detail?: string };

/**
 * Admin + debug-mode only: replays the recorded UI clicks for this order
 * (see useClickTrail) interleaved with the chat messages, so "what did they
 * actually do" reads as one timeline.
 */
const ClickTrailPanel: React.FC<{ orderId: number; messages: Message[] }> = ({ orderId, messages }) => {
  const { currentUser } = useAuth();
  const { debugMode } = useDebugMode();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clicks, setClicks] = useState<ClickEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setOpen(true);
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.get(`/api/user/orders/${orderId}/clicks`);
      setClicks(res.data?.events || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load the trail');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  const rows: Row[] = useMemo(() => {
    const fromClicks: Row[] = (clicks || []).map(c => ({
      at: new Date(c.clicked_at),
      kind: 'click' as const,
      label: c.label,
      detail: [c.path, c.detail && Object.keys(c.detail).length ? JSON.stringify(c.detail) : null]
        .filter(Boolean)
        .join('  '),
    }));
    const fromMessages: Row[] = messages.map(m => ({
      at: new Date(m.timestamp),
      kind: m.sender === 'user' ? ('user' as const) : ('bot' as const),
      label: (m.text || m.text_message || '').trim().slice(0, 120) || `(${m.sender} message)`,
    }));
    return [...fromClicks, ...fromMessages].sort((a, b) => a.at.getTime() - b.at.getTime());
  }, [clicks, messages]);

  if (currentUser?.role !== 'admin' || !debugMode) return null;

  return (
    <>
      <button
        type="button"
        onClick={load}
        title="Admin only — replay the clicks recorded on this order"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#efebe2] bg-[#f6f4f0] text-[#999999] hover:bg-[#efebe2] hover:text-[#201e1a] text-xs font-medium flex-shrink-0 cursor-pointer"
      >
        <History className="w-3.5 h-3.5" />
        Trail
      </button>

      {open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[80vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#efebe2]">
              <History className="w-4 h-4 text-[#201e1a]" />
              <span className="font-semibold text-sm text-[#201e1a]">Click trail — order {orderId}</span>
              <button type="button" onClick={() => setOpen(false)} className="ml-auto text-[#999999] hover:text-[#201e1a]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="overflow-y-auto p-3 text-xs font-mono">
              {loading && <div className="flex items-center gap-2 text-[#6B6862] p-3"><Loader2 className="w-4 h-4 animate-spin" /> loading…</div>}
              {error && <div className="text-red-600 p-3">{error}</div>}
              {!loading && !error && rows.length === 0 && <div className="text-[#6B6862] p-3">No clicks recorded yet — check the <code>order_click_trail</code> feature flag is on for this org.</div>}
              {rows.map((r, i) => (
                <div key={i} className="flex gap-3 py-1 border-b border-[#f6f4f0] last:border-0">
                  <span className="text-[#999999] whitespace-nowrap">{r.at.toLocaleTimeString()}</span>
                  <span className={`w-12 flex-shrink-0 ${r.kind === 'click' ? 'text-[#6866ff]' : 'text-[#6B6862]'}`}>{r.kind}</span>
                  <span className="text-[#201e1a] break-all">
                    {r.label}
                    {r.detail && <span className="text-[#999999]"> · {r.detail}</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ClickTrailPanel;
